import { createRoot } from "react-dom/client";
import App from "./App.tsx";
import "./index.css";
import { autoLoadDemoData } from "./lib/demoData";

// Auto-load demo data (forum posts, listings) into localStorage if not present
autoLoadDemoData();

// Safe sessionStorage wrapper to prevent crashes in private browsing
const safeSessionGet = (key: string): string | null => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeSessionSet = (key: string, value: string): void => {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    // Silently fail if storage is unavailable
  }
};

// In development, ensure any previously-registered service worker is removed.
// Stale SW caches can break Vite's dynamic imports ("Failed to fetch dynamically imported module").
// NOTE: unregistering does NOT immediately stop a SW from controlling the current page;
// a one-time reload after cleanup is the most reliable fix.
if (import.meta.env.DEV && "serviceWorker" in navigator) {
  const didReloadKey = "__dev_sw_cleanup_reloaded_v2__";

  navigator.serviceWorker.getRegistrations().then(async (regs) => {
    const hadRegs = regs.length > 0;

    await Promise.all(regs.map((reg) => reg.unregister()));

    let hadCaches = false;
    try {
      if ("caches" in window) {
        const keys = await caches.keys();
        hadCaches = keys.length > 0;
        await Promise.all(keys.map((k) => caches.delete(k)));
      }
    } catch {
      // caches API may be restricted in some environments
    }

    // Reload once if we actually cleaned anything.
    if ((hadRegs || hadCaches) && !safeSessionGet(didReloadKey)) {
      safeSessionSet(didReloadKey, "1");
      window.location.reload();
    }
  });
}

// Handle dynamic import failures — reload only once per session, with a cooldown
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason?.message?.includes('Failed to fetch dynamically imported module')) {
    const reloadKey = '__dynamic_import_reload__';
    const lastReload = safeSessionGet(reloadKey);
    const now = Date.now();
    // Only reload if we haven't reloaded in the last 30 seconds
    if (!lastReload || (now - Number(lastReload)) > 30000) {
      safeSessionSet(reloadKey, String(now));
      window.location.reload();
    }
  }
});

// When PWA returns to foreground (e.g. iOS user switches back to app),
// check for SW updates. This is critical for iOS standalone PWA where
// the SW update lifecycle is unreliable.
if ('serviceWorker' in navigator && !import.meta.env.DEV) {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      navigator.serviceWorker.getRegistration().then((reg) => {
        if (reg) {
          reg.update().catch(() => {
            // Silently fail - will retry on next visibility change
          });
        }
      });
    }
  });

  // Also check when app comes online after being offline
  window.addEventListener('online', () => {
    navigator.serviceWorker.getRegistration().then((reg) => {
      if (reg) reg.update().catch(() => {});
    });
  });

  // When a new Service Worker takes control (after skipWaiting), reload the page
  // so clients immediately use the latest cached assets.
  // Use a timestamp-based cooldown to prevent infinite reload loops.
  const swReloadKey = '__sw_controller_change_reload__';
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    const lastReload = safeSessionGet(swReloadKey);
    const now = Date.now();
    if (!lastReload || (now - Number(lastReload)) > 30000) {
      safeSessionSet(swReloadKey, String(now));
      window.location.reload();
    }
  });
}

createRoot(document.getElementById("root")!).render(<App />);
